import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RgbaColor } from '@uiw/color-convert';
import ColorComponent from '../../../../../../../components/channel-details-controls/light/color';
import RgbPicker from '../../../../../../../components/channel-details-controls/light/picker';
// import Scenes from './scenes';
import TemperatureComponent from '../../../../../../../components/channel-details-controls/light/temperature';
import LavvaDetailsWrapper from '../../../../../../../components/details-wrapper';
import { PICKER_COLOR_LIST } from '../../../../../../../const';
import { ChannelTypeInternal } from '../../../../../../../data-access/gql-types/graphql';
import { rgbaToRgbString } from '../../../../../../../utils/helpers';
import { useLight } from '../../../../../hooks/light';
import { useChannelsStateStore } from '../../../../../store/channels-state';
import { ChannelType, ChannelLightStateType, RGBType } from '../../../../../types';
import { ChannelTimesActivations } from '../../../components/channel-times-activation';
import { useRgb } from '../../hooks/use-rgb';
import CurrentlyComponent from './currently';

const scenesVisible = false;

type ChannelDetailsLightRgbProps = {
  channel: ChannelType;
};

export const ChannelDetailsLightRgb: React.FC<ChannelDetailsLightRgbProps> = ({ channel }) => {
  const { t } = useTranslation('channel-details');
  const state = useChannelsStateStore((s) => s.channelState[channel.id]) as ChannelLightStateType | null;
  const [rgbValue, setRgbValue] = useState<string>('');
  const [brightness, setBrightness] = useState<number>(state?.brightness ?? 100);
  const [temperature, setTemperature] = useState<number>(0);
  const [preventSwipeViews, setPreventSwipeViews] = useState<boolean>(false);
  const { handleControlOn, handleControlBrightness, handleControlTemperature, handleControlRgb, isOn } =
    useLight(channel);
  const { rgbType, hasBrightness } = useRgb(channel);
  const colorState = state?.color;

  useEffect(() => {
    if (channel.data.type !== ChannelTypeInternal.Light) return;
    setBrightness(state?.brightness ?? 100);
    if (rgbType === RGBType.WW) setTemperature(state?.temperature ?? 100);

    if (colorState) {
      setRgbValue(`${colorState?.r},${colorState?.g},${colorState?.b}`);
    }

    if (colorState?.r === 255 && colorState?.g === 255 && colorState?.b === 255) setTemperature(45);
    if (colorState?.r === 255 && colorState?.g === 255 && colorState?.b !== 255 && rgbType !== RGBType.WW) {
      setTemperature(Math.round((38 * colorState?.b) / 255));
    }
    if (colorState?.r !== 255 && colorState?.g === 255 && colorState?.b === 255 && rgbType !== RGBType.WW) {
      setTemperature(Math.round((54 * 255) / colorState?.r));
    }
  }, [JSON.stringify(channel?.data), rgbType, state?.brightness, colorState]);

  useEffect(() => {
    const timeout = setTimeout(() => setPreventSwipeViews(false), 950);
    return () => clearTimeout(timeout);
  }, [rgbValue]);

  const handleBrightness = (brightness: number) => {
    setBrightness(brightness);
    handleControlBrightness(brightness);
  };

  const handleTemperature = (event: React.PointerEvent<HTMLInputElement>) => {
    const value = Number((event.target as HTMLInputElement).value);
    setTemperature(value);
    let rgb: string;

    if (value <= 38) rgb = `255,255,${Math.round((value * 255) / 38)}`;
    else if (value > 38 && value < 54) rgb = '255,255,255';
    else rgb = `${Math.round((54 * 255) / value)},255,255`;

    if (rgbType === RGBType.WW) {
      handleControlTemperature(value);
    } else {
      setRgbValue(rgb);
      handleControlRgb(rgb);
    }
  };

  const handleChange = (val: number) => {
    setPreventSwipeViews(true);
    setBrightness(val);
  };

  const onChangeTemperature = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setTemperature(value);
    let rgb: string;

    if (value <= 38) {
      rgb = `255,255,${Math.round((value * 255) / 38)}`;
    } else if (value > 38 && value < 54) {
      rgb = '255,255,255';
    } else {
      rgb = `${Math.round((54 * 255) / value)},255,255`;
    }

    setRgbValue(rgb);
  };

  const handleColorPicked = (rgba: RgbaColor) => {
    const rgb = rgbaToRgbString(rgba);

    setRgbValue(rgb);
    handleControlRgb(rgb);
  };

  const tabs = [
    ...(rgbType === RGBType.RGB || rgbType === RGBType.RGBW ? [{ label: t('rgb') }] : []),
    ...(rgbType === RGBType.RGB || rgbType === RGBType.RGBW ? [{ label: t('color') }] : []),
    ...(rgbType === RGBType.WHITE || rgbType === RGBType.WW ? [{ label: t('control') }] : []),
    ...(rgbType !== RGBType.WHITE ? [{ label: t('white') }] : []),
    ...(scenesVisible ? [{ label: t('scenes') }] : []),
  ];

  const components = useMemo(() => {
    const list = [
      <>
        <RgbPicker
          key={0}
          rgbValue={rgbValue}
          handleColorPicked={handleColorPicked}
          isOn={isOn}
          setPreventSwipeViews={setPreventSwipeViews}
          {...(hasBrightness ? { handleChange } : {})}
          {...(hasBrightness ? { brightness } : {})}
          {...(hasBrightness ? { handleBrightness } : {})}
        />
        <ChannelTimesActivations channel={channel} />
      </>,
      <ColorComponent
        key={1}
        rgbValue={rgbValue}
        handleChange={handleChange}
        brightness={brightness}
        handleBrightness={handleBrightness}
        handleControlRgb={handleControlRgb}
        colors={PICKER_COLOR_LIST}
        darkBorder
      />,
      <>
        <CurrentlyComponent
          key={2}
          isOn={isOn}
          handleControlOn={handleControlOn}
          {...(hasBrightness ? { handleChange } : {})}
          {...(hasBrightness ? { brightness } : {})}
          {...(hasBrightness ? { handleBrightness } : {})}
        />
        <ChannelTimesActivations channel={channel} />
      </>,
      <TemperatureComponent
        key={3}
        temperature={temperature}
        onChangeTemperature={onChangeTemperature}
        handleTemperature={handleTemperature}
        {...(hasBrightness ? { handleChange } : {})}
        {...(hasBrightness ? { brightness } : {})}
        {...(hasBrightness ? { handleBrightness } : {})}
      />,
    ];

    if (rgbType === RGBType.WHITE) return [list[2]];
    if (rgbType === RGBType.WW) return [list[2], list[3]];
    if (rgbType === RGBType.RGB || rgbType === RGBType.RGBW) return [list[0], list[1], list[3]];

    return [];
  }, [rgbValue, isOn, brightness, temperature, PICKER_COLOR_LIST, rgbType, channel]);

  return (
    <LavvaDetailsWrapper
      tabs={tabs}
      additionalTabs={rgbType !== RGBType.WHITE ? components.slice(1) : []}
      preventSwipeEvent={preventSwipeViews}
    >
      {components.length ? components[0] : null}
    </LavvaDetailsWrapper>
  );
};
